import React from 'react';
import { Info, Camera, MapPin, AlertTriangle, Shield, HelpCircle, Zap } from 'lucide-react';

/**
 * Компонент с информацией о EXIF-данных, их значении и рекомендациях по использованию
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-purple-500" />
        Всё о EXIF-данных: что это такое и зачем они нужны
      </h2>
      
      <div className="prose prose-purple dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-purple-500" />
          Что такое EXIF-данные?
        </h3>
        <p>
          <strong>EXIF (Exchangeable Image File Format)</strong> — это стандарт, который определяет формат метаданных, 
          встраиваемых в файлы изображений. Эти метаданные содержат техническую информацию о снимке, настройках камеры и условиях съемки.
        </p>
        <p>
          Практически все современные цифровые камеры, смартфоны и другие устройства с функцией съемки автоматически добавляют 
          EXIF-данные к создаваемым изображениям. Эта информация сохраняется внутри файла и передается вместе с ним при копировании или отправке.
        </p>

        <h3 className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-purple-500" />
          Какую информацию содержат EXIF-данные?
        </h3>
        <ul>
          <li><strong>Общая информация о файле:</strong> тип файла, размер, разрешение, дата создания и изменения</li>
          <li><strong>Информация о камере:</strong> производитель и модель устройства, тип и серийный номер объектива</li>
          <li><strong>Настройки съемки:</strong> фокусное расстояние, диафрагма, выдержка, ISO, режим экспозиции, использование вспышки</li>
          <li><strong>Данные о местоположении:</strong> GPS-координаты места съемки (если включена соответствующая функция)</li>
          <li><strong>Авторская информация:</strong> имя фотографа, копирайт, описание, ключевые слова</li>
          <li><strong>Миниатюра изображения:</strong> уменьшенная копия снимка для предварительного просмотра</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Интересный факт</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            В России многие профессиональные фотографы используют EXIF-данные как доказательство авторства при регистрации прав на 
            произведения в Российском авторском обществе (РАО). Эти метаданные могут служить дополнительным подтверждением в спорах 
            об интеллектуальной собственности.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-500" />
          Для чего используются EXIF-данные?
        </h3>
        <ul>
          <li><strong>Для фотографов:</strong> анализ технических параметров успешных снимков, отслеживание прогресса и совершенствование техники съемки</li>
          <li><strong>Для редакторов:</strong> понимание условий съемки для более точной обработки изображений</li>
          <li><strong>Для организации архива:</strong> сортировка и каталогизация фотографий по дате, местоположению и другим параметрам</li>
          <li><strong>Для подтверждения подлинности:</strong> проверка оригинальности фотографии и обнаружение следов редактирования</li>
          <li><strong>Для геотегинга:</strong> привязка фотографий к карте и создание фотоотчетов о путешествиях</li>
          <li><strong>Для защиты авторских прав:</strong> встраивание информации об авторе и условиях использования фотографии</li>
        </ul>

        <p>
          В России особенно актуально использование EXIF-данных для защиты авторских прав на фотографии. 
          Согласно 4-й части Гражданского кодекса РФ, фотографии относятся к объектам авторского права, а EXIF-метаданные 
          могут служить одним из доказательств авторства и времени создания произведения.
        </p>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Риски и проблемы, связанные с EXIF-данными
        </h3>
        <p>
          Несмотря на полезность EXIF-данных, они могут представлять определенные риски для приватности:
        </p>
        <ul>
          <li><strong>Раскрытие местоположения:</strong> GPS-координаты могут указывать на ваш дом, работу или часто посещаемые места</li>
          <li><strong>Личная информация:</strong> имя автора, серийные номера устройств могут использоваться для идентификации</li>
          <li><strong>Временные метки:</strong> раскрывают, когда и где вы находились в определенное время</li>
          <li><strong>Ценное оборудование:</strong> информация о дорогой технике может привлечь внимание злоумышленников</li>
        </ul>

        <p>
          В 2023 году Роскомнадзор выпустил рекомендации по защите персональных данных в социальных сетях, где отдельно упоминается 
          необходимость удаления метаданных из фотографий перед их публикацией в открытом доступе.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Реальный случай из российской практики
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            В 2021 году в российских СМИ освещался случай, когда злоумышленники использовали GPS-метаданные из фотографий, 
            опубликованных в социальных сетях, для определения адресов потенциальных жертв краж. Они анализировали профили 
            пользователей, которые делились фотографиями дорогих вещей, и по EXIF-данным определяли их местожительство.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Как защитить свою приватность?
        </h3>
        <ul>
          <li><strong>Отключите геотегинг:</strong> В настройках камеры или смартфона отключите запись GPS-координат</li>
          <li><strong>Удаляйте метаданные:</strong> Перед публикацией в интернете используйте инструменты для очистки EXIF-данных</li>
          <li><strong>Проверяйте перед публикацией:</strong> Используйте считыватели EXIF-данных (как этот инструмент) для проверки содержимого ваших фотографий</li>
          <li><strong>Используйте безопасные платформы:</strong> Многие социальные сети (ВКонтакте, Telegram) автоматически удаляют EXIF-данные при загрузке, но лучше перестраховаться</li>
          <li><strong>Редактируйте метаданные:</strong> Вместо полного удаления можно оставить только безопасную информацию, удалив конфиденциальные данные</li>
        </ul>

        <p>
          В соответствии с российским законодательством о защите персональных данных, рекомендуется проявлять особую осторожность 
          при публикации фотографий с геометками, особенно если на них изображены дети или другие лица, не дававшие согласия на публикацию.
        </p>

        <h3>Как работает наш считыватель EXIF-данных?</h3>
        <p>
          Наш инструмент анализирует EXIF-метаданные ваших изображений непосредственно в браузере, без загрузки файлов на сервер. 
          Это обеспечивает полную приватность и безопасность ваших данных. После анализа вы получаете полную информацию о метаданных, 
          можете оценить риски для приватности и при необходимости отредактировать или удалить чувствительную информацию.
        </p>

        <p>
          Инструмент разработан с учетом российских стандартов и рекомендаций по защите персональных данных, предоставляя 
          пользователям простой и удобный способ контролировать информацию, которую они делят при публикации фотографий.
        </p>

        <p className="font-semibold mt-6">
          Регулярно проверяйте EXIF-данные ваших фотографий перед публикацией, чтобы защитить свою приватность и безопасность!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;